// Internal modules
import { appStore } from '@/store';
import { features, logger } from '@/lib';
import { fetchAndResetContent } from '@/lib/content';
import { updateLocale } from '@/lib/globals';
import { noop, redirectToGenericError } from '@/lib/utils';
import { BATCHED_QUERY_OPTIONS_OVERRIDES } from './constants';
import {
  queryCookieBanner,
  removeCookieBanner,
  setCookieBanner,
} from '../../shared';
import {
  getSuggestedDefaultLanguageForCountry,
  queryInitialData,
  readSuggestedLanguagesQuery,
} from './graphql';
import {
  logCountryChangeAttemptToFPTI,
  logCountryChangeErrorToFPTI,
  logCountryChangeSuccessToFPTI,
} from './fpti';
import {
  cacheInitialDataInReduxStore,
  fetchCheckoutSessionData,
  fetchLocaleMetadata,
  // @ts-expect-error untyped import
} from '@/components/App/api';
import { conditionallyCalculateShippingAndTaxes } from '@/components/Shipping/utils';
import {
  handleBlockedDomesticTransactions,
  handleSignupAndGuestIneligible,
} from '@/components/Billing/utils';
import type { CountryCodes } from '@/generated/graphql';

/**
 * When the user changes country we need to do the following:
 * 1. Remove the cookie banner if it is present
 * 2. Toggle the page loading spinner, which will be shown while
 *    we fetch all the necessary new data
 * 3. Get the suggested default language for the new country. This is
 *    needed to fetch the correct content as well as the correct
 *    locale metadata.
 * 4. Update the locale - this needs to be done before we make any API
 *    calls so that the correct country and language headers are set
 *    on the API requests. The checkoutSession request relies on the
 *    resolved locale for formatting the cart amounts, not an argument
 *    passed to the query. The cookie banner also relies on the locale
 *    instead of an argument.
 * 5. Fetch new data
 *    CDN:
 *      - content for new country/language combo
 *    API:
 *      - Locale metadata for country/language
 *      - Cart amounts to make sure they are correctly formatted
 *      - Cookie banner - will only be shown if the user did not already
 *        accept a previously shown cookie banner.
 *      - Initial data (elmo experiments, checkout session flags, etc)
 * 6. Reset the store and the content, show the cookie banner if it is
 *    required and finally hide the loading spinner.
 */
const handleCountryChange = (
  {
    fromCountry,
    toCountry,
  }: { fromCountry: CountryCodes; toCountry: CountryCodes },
  onShippingCallback = noop,
): Promise<void> => {
  // If the `blockNonDomesticPayer` flag is active then the user is not
  // allowed to change countries.
  if (features.isActive('blockNonDomesticPayer')) {
    // @ts-expect-error Make this return a promise
    return logger.info('non_domestic_payer_country_change_blocked', {
      fromCountry,
      toCountry,
    });
  }

  removeCookieBanner();
  appStore.getActions().ui.toggleIsPageLoading(true);
  logCountryChangeAttemptToFPTI({ fromCountry, toCountry });

  const territories = readSuggestedLanguagesQuery();
  const suggestedDefaultLanguage = getSuggestedDefaultLanguageForCountry({
    countryCode: toCountry,
    // @ts-expect-error type readSuggestedLanguagesQuery
    territories,
  });

  updateLocale({
    country: toCountry,
    // @ts-expect-error Type GlobalLocality
    language: suggestedDefaultLanguage,
  });

  const { form: formState } = appStore.getState();
  const { shippingAddress } = formState;

  // Fix for LI-12385: shipping country code should be passed as 2nd param for
  // fetchLocaleMetadata to load griffin data. Previously we passed toCountry
  // which is redundant as fetchLocaleMetadata grabs billing country from locale
  const shippingCountryCode = shippingAddress?.country?.value ?? toCountry;

  /**
   * @TO_DO @APOLLO_MIGRATION
   * When migrating to Apollo Client for state management switch to using
   * the checkoutSessionQuery from `lib/graphql`. I'm reusing this
   * query for now since the data needs to be saved in the redux store so it
   * can be accessed by components that need it.
   */
  const promises = [
    queryCookieBanner(),
    fetchLocaleMetadata(BATCHED_QUERY_OPTIONS_OVERRIDES, shippingCountryCode),
    fetchCheckoutSessionData({
      fetchPolicy: 'network-only',
      ...BATCHED_QUERY_OPTIONS_OVERRIDES,
    }),
    fetchAndResetContent(),
    queryInitialData({
      countryCode: toCountry,
      languageCode: suggestedDefaultLanguage,
    }),
  ];

  return Promise.all(promises)
    .then(([cookieBanner, localeMetadata, checkoutSession]) => {
      logCountryChangeSuccessToFPTI({ fromCountry, toCountry });
      appStore.getActions().ui.toggleIsPageLoading(false);
      cacheInitialDataInReduxStore({
        checkoutSessionResponse: checkoutSession,
        localeMetadataResponse: localeMetadata,
        shippingCountryCode,
      });
      appStore.getActions().ui.setIsReduxStoreHydrated(true);
      setCookieBanner(cookieBanner);
    })
    .then(() => {
      handleBlockedDomesticTransactions();
      handleSignupAndGuestIneligible();
      conditionallyCalculateShippingAndTaxes();
      onShippingCallback();
    })
    .catch(error => {
      logCountryChangeErrorToFPTI({
        fromCountry,
        int_error_code: error.message,
        int_error_desc: 'country_change_contingency',
        toCountry,
      });
      redirectToGenericError();
    });
};

export { handleCountryChange };
